import type { Action, Dispatch } from 'redux';
import type { ThunkAction, ThunkDispatch } from 'redux-thunk';
import type { IAppState } from './appstate';
import { toastr } from './helpers/Toastr';

export type RemoteData<T, E> = Initial | Loading | Success<T> | Failure<E>;

export interface Failure<E> {
	type: 'failure';
	err: E;
}
export interface Success<T> {
	type: 'success';
	data: T;
}
export interface Initial {
	type: 'initial';
}
export interface Loading {
	type: 'loading';
}

export function isRemoteSuccess<T, E>(
	data: RemoteData<T, E>
): data is Success<T> {
	return data.type === 'success';
}

export type RemoteState = 'initial' | 'loading' | 'success' | 'failure';

/**
 * A func to make network requests
 * @template TArgs The type of args object passed from the caller
 * @template TData The type of data used in the Success action
 */
export type IO<TArgs, TData, TFailureData> = (
	args: TArgs,
	dispatch: Dispatch<any>,
	getState: () => IAppState
) => Promise<IOResult<TData, TFailureData>>;

type IOResult<TSuccessPayload, TFailurePayload> =
	| TSuccessPayload // 1
	| ApiSuccess<TSuccessPayload> // 2
	| ApiNotSuccess // 3
	| [ApiNotSuccess, TFailurePayload]; // 4

export function genericErrorToast(res: ApiNotSuccess) {
	if (res.kind === 'failure') {
		const body = `There was an issue with your request: ${res.errors.join(
			', '
		)}`;
		toastr.error({
			title: 'Validation Error',
			body,
		});
		return;
	}

	// TODO exceptions
	// TODO network errors
}

/**
 * @deprecated Use `@reduxjs/toolkit`
 *
 * Creates a strongly typed action creator that will
 * send out a XYZ_PENDING action, followed by some IO,
 * followed by either a XYZ_SUCCESS or XYZ_FAILURE.
 * @param pending The type string for the pending action.
 * @param failure The type string for the failure action.
 * @param success The type string for the success action.
 * @param io A func that will recieve the callers args, do some IO, and return the data, or some failure.
 * @param pendingPayload a func that returns some data to send on the pending action.
 */
export function createFetchAction<
	TArgs,
	TPending extends { type: TPending['type']; payload?: TPending['payload'] },
	TFailure extends {
		type: TFailure['type'];
		payload?: TFailure['payload'];
		errors: string[];
	},
	TSuccess extends { type: TSuccess['type']; payload: TSuccess['payload'] }
>(
	pending: TPending['type'],
	failure: TFailure['type'],
	success: TSuccess['type'],
	io: IO<TArgs, TSuccess['payload'], TFailure['payload']>,
	pendingPayload: (args: TArgs) => TPending['payload'] = () => undefined,
	afterSuccess?: (dispatch: Dispatch<any>, getState: () => IAppState) => any
): AppThunk<TArgs> {
	const thunk = createThunk<TArgs>(async (args, dispatch, getState) => {
		const pendingAction = createPendingAction<TPending>(
			pending,
			pendingPayload(args)
		);
		dispatch(pendingAction);

		const ioResult = await io(args, dispatch, getState);

		if (typeof ioResult === 'undefined') {
			return;
		}

		if (isFailureTuple<TFailure['payload']>(ioResult)) {
			// IOResult 4
			const [apiRes, payload] = ioResult as [
				ApiNotSuccess,
				TFailure['payload']
			];
			const errors = getErrors(apiRes);
			const failureAction = createFailureAction<TFailure>(
				failure,
				errors,
				payload
			);
			dispatch(failureAction);
			return;
		}

		if (isApiKind(ioResult)) {
			if (isApiNotSuccess(ioResult)) {
				// IOResult 3
				const errors = getErrors(ioResult);
				const failureAction = createFailureAction<TFailure>(failure, errors);
				dispatch(failureAction);
				return;
			}

			if (ioResult.ok) {
				// IOResult 2
				const data = ioResult.blob || ioResult.value;

				const successActionCreator =
					createPayloadActionCreator<TSuccess>(success);
				const successAction = successActionCreator(data);

				dispatch(successAction);
				if (afterSuccess) {
					dispatch(afterSuccess);
				}
				return;
			}
		}

		{
			// IOResult 1
			const data = ioResult as TSuccess['payload'];

			const successActionCreator =
				createPayloadActionCreator<TSuccess>(success);
			const successAction = successActionCreator(data);

			dispatch(successAction);
			if (afterSuccess) {
				dispatch(afterSuccess);
			}
			return;
		}
	});

	return thunk;
}

export type AppThunk<TArgs> = TArgs extends void
	? () => ThunkAction<Promise<void>, IAppState, unknown, Action<string>>
	: (
			args: TArgs
	  ) => ThunkAction<Promise<void>, IAppState, unknown, Action<string>>;

/**
 * When creating a thunk, this function will be run at the time of dispatch, and provided the args from the caller, Redux's
 * dispatch function, and the getState function.
 */
export type CreateThunkArgs<TArgs> = (
	args: TArgs,
	dispatch: ThunkDispatch<IAppState, unknown, Action<any>>,
	getState: () => IAppState
) => Promise<void>;

/**
 * Creates a thunk for Redux to evaluate. Includes arguments, dispatching, state access, network access, and other forms of I/O.
 * By convention, this should dispatch a pending action before beginning remote access, and a success or failure action after completion.
 * @template TArgs the type of args which are provided by the caller.
 * @param io A function which accesses all needed remote data via HTTP, WebSockets, or browser APIs.
 */
export function createThunk<TArgs = void>(
	io: CreateThunkArgs<TArgs>
): AppThunk<TArgs> {
	const argTaker = (args: TArgs) => {
		const thunk: ThunkAction<
			Promise<void>,
			IAppState,
			unknown,
			Action<any>
		> = async (dispatch, getState) => {
			await io(args, dispatch, getState);
		};

		return thunk;
	};

	return argTaker as AppThunk<TArgs>;
}

export type AsyncThunksAsValidDispatches<
	TObj extends Record<string, AppThunk<any>>
> = {
	[K in keyof TObj]: (...args: Parameters<TObj[K]>) => void;
};

export type SyncActionsAsValidDispatches<TObj extends Record<string, any>> = {
	[K in keyof TObj]: (...args: Parameters<TObj[K]>) => void;
};

/**
 * Creates an object which describes all of the actions available on a slice (both sync and async.)
 * This function is used to easily connect page components to their corresponding slice.
 */
export function createDispatchProps<
	TActions extends Record<string, any>,
	TAsyncActions extends Record<string, AppThunk<any>>
>(actions: TActions, asyncActions: TAsyncActions) {
	return {
		...(actions as unknown as SyncActionsAsValidDispatches<typeof actions>),
		...(asyncActions as unknown as AsyncThunksAsValidDispatches<
			typeof asyncActions
		>),
	};
}

function isApiNotSuccess(
	apiResult: ApiResult<any>
): apiResult is ApiNotSuccess {
	return !apiResult.ok;
}

function isFailureTuple<TPayload>(arr: any): arr is [ApiNotSuccess, TPayload] {
	return Array.isArray(arr) && arr[0] && arr[0].namespace === 'ui-api-result';
}

function getErrors(apiResult: ApiNotSuccess) {
	let errors: string[] = [];

	switch (apiResult.kind) {
		case 'failure': {
			errors = apiResult.errors;
			break;
		}
		case 'error': {
			const code = apiResult.response.status;
			errors = [
				`The server could not respond to the request: HTTP Status ${code}`,
			];
			break;
		}
		case 'exception': {
			const msg = apiResult.ex.message;
			errors = [msg];
			break;
		}
	}

	return errors;
}

function createPendingAction<
	TAction extends {
		type: TAction['type'];
		payload?: TAction['payload'];
	}
>(
	type: TAction['type'],
	payload: TAction['payload'] | undefined
): { type: TAction['type']; payload?: TAction['payload'] } {
	if (typeof payload !== 'undefined') {
		return {
			type,
			payload,
		};
	}

	return {
		type,
	};
}

function createFailureAction<
	TAction extends {
		type: TAction['type'];
		errors: string[];
		payload?: TAction['payload'];
	}
>(actionType: TAction['type'], errors: string[], payload?: TAction['payload']) {
	return { type: actionType, errors, payload };
}

function createPayloadActionCreator<
	TAction extends { type: TAction['type']; payload: Blob | TAction['payload'] }
>(typeName: TAction['type']) {
	return (data: Blob | TAction['payload']) => ({
		type: typeName,
		payload: data,
	});
}

function isApiKind<TData>(
	data: TData | ApiResult<TData>
): data is ApiResult<TData> {
	return (data as ApiResult<TData>).namespace === 'ui-api-result';
}

/** Checks the collection to see if any of the items failed. */
export function allOk<T>(
	arr: ApiResult<T>[]
): CollectionFail | CollectionSuccess<T> {
	if (arr.some((x) => x.kind !== 'success')) {
		return {
			ok: false,
			failures: arr.filter(isNotSuccess),
		};
	}

	// if all were successful, this won't filter anything, but it narrows the type down.
	return {
		ok: true,
		values: arr.filter(isSuccess),
	};
}

interface CollectionFail {
	ok: false;
	failures: ApiNotSuccess[];
}

interface CollectionSuccess<TData> {
	ok: true;
	values: ApiSuccess<TData>[];
}

// =========================
// API Result Types
// =========================

export type ApiResult<TData> = ApiSuccess<TData> | ApiNotSuccess;

export type ApiNotSuccess = ApiFailure | ApiError | ApiException;

export function isSuccess<TData>(
	res: ApiResult<TData>
): res is ApiSuccess<TData> {
	return res.kind === 'success';
}

export function isNotSuccess<TData>(
	res: ApiResult<TData>
): res is ApiFailure | ApiError | ApiException {
	return res.kind !== 'success';
}

interface ApiBase {
	hasErrors: false;
	metaData: Record<string, string>;
}

/** HTTP status reported OK, and API response indicated no errors. */
export interface ApiSuccess<TData> extends ApiBase {
	namespace: 'ui-api-result'; // this smells
	ok: true;
	kind: 'success';
	value: TData;
	blob?: Blob;
}

/** HTTP status reported OK, but API response indicated errors. */
export interface ApiFailure extends ApiBase {
	namespace: 'ui-api-result'; // this smells
	ok: false;
	kind: 'failure';
	errors: string[];
}

/** HTTP status reported an error.  */
export interface ApiError extends ApiBase {
	namespace: 'ui-api-result'; // this smells
	ok: false;
	kind: 'error';
	response: Response;
}

/** Fetch process threw an exception. */
export interface ApiException extends ApiBase {
	namespace: 'ui-api-result'; // this smells
	ok: false;
	kind: 'exception';
	ex: Error;
}
